import { Link } from "react-router-dom"
import { useState } from "react"
import { supabase } from "../services/supabase"
import { createLogger } from '../utils/logger'
import { useRedirectIfAuthenticated } from '../hooks/useRedirectIfAuthenticated'

const logger = createLogger('auth')

export default function ForgotPasswordPage() {
  useRedirectIfAuthenticated()
  const loginPath = "/login"

  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    const redirectTo = `${window.location.origin}/reset-password`
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    })
    setLoading(false)
    if (resetError) {
      logger.warn('Password reset email failed', { view: 'forgot_password', action: 'reset_password_email' }, resetError)
      setError(resetError.message)
      return
    }
    setSent(true)
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-10 block">
          <img
            src="/ls-logo.svg"
            alt="LandSentry"
            className="mx-auto h-14 w-auto md:h-16"
            width={280}
            height={70}
          />
        </Link>

        <div className="w-full max-w-sm rounded-lg border border-neutral-200 bg-white p-8 shadow-sm">
          <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Reset password</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Enter your account email and we&apos;ll send you a link to choose a new password.
          </p>

          {error && (
            <div
              className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              {error}
            </div>
          )}

          {sent ? (
            <div className="mt-6 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-3 text-sm text-neutral-800">
              If an account exists for <strong>{email.trim()}</strong>, you&apos;ll receive an email with a reset link
              shortly. Check your spam folder.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="forgot-email" className="sr-only">
                  Email
                </label>
                <input
                  id="forgot-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  placeholder="Email"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900 disabled:bg-neutral-50"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  "Send reset link"
                )}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm">
            <Link to={loginPath} className="font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900">
              Back to sign in
            </Link>
          </p>
        </div>

        <Link
          to="/"
          className="mt-10 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  )
}
